import { useState } from 'react'
import { useNavigate } from 'react-router'
import { InlineMfaPrompt, LoginDialog } from 'common-ui/components/LoginDialog'
import { useAuth } from './Auth'
import { useBillingApi } from '../../api/billing/billing-context'
import { INITIAL_PATH, Path } from '../../routes/path'
import netiLogoWhite from '../../assets/netinsight-logo-color@3x.png'
import { passkeysSupported } from 'common-ui/passkeys'
import { TotpLoginDialog, totpLoginError } from 'common-ui/components/TotpManager'
import { LoginResult, TotpLoginChallenge } from 'common-billing-server/types'

export const LoginPage = () => {
    const auth = useAuth()
    const navigate = useNavigate()
    const billingApi = useBillingApi()
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | undefined>()
    const [mfaError, setMfaError] = useState<string>()
    const [mfaChallenge, setMfaChallenge] = useState<TotpLoginChallenge>()

    const finishLogin = async (result: LoginResult, skipPasskeyPrompt = false) => {
        await auth.refreshUser()
        const promptPasskey = !skipPasskeyPrompt && result.passkeyRegistrationRequired && passkeysSupported()
        let destination = INITIAL_PATH
        let state: { promptPasskey: true } | undefined
        if (promptPasskey) {
            destination = Path.profile
            state = { promptPasskey: true }
        }
        navigate(destination, {
            replace: true,
            state,
        })
    }

    const handleLogin = async ({ username, password, otp }: { username: string; password: string; otp?: string }) => {
        if (isLoading) return
        setError(undefined)
        setMfaError(undefined)
        setIsLoading(true)
        if (mfaChallenge?.state === 'totpRequired') {
            try {
                if (!otp) return
                const result = await billingApi.totpLogin.complete(mfaChallenge.challengeId, otp)
                await finishLogin(result)
            } catch (error) {
                setMfaError(totpLoginError(error))
            } finally {
                setIsLoading(false)
            }
            return
        }
        try {
            const result = await auth.login(username, password)
            if (result.state === 'authenticated') await finishLogin(result)
            else setMfaChallenge(result)
        } catch {
            setError('Log in failed')
        }
        setIsLoading(false)
    }

    const clearInlineMfa = () => {
        if (mfaChallenge?.state !== 'totpRequired') return
        const challengeId = mfaChallenge.challengeId
        setMfaChallenge(undefined)
        setMfaError(undefined)
        void billingApi.totpLogin.abandon(challengeId).catch(() => undefined)
    }

    const signInWithPasskey = async () => {
        await auth.loginWithPasskey()
        if (mfaChallenge) void billingApi.totpLogin.abandon(mfaChallenge.challengeId).catch(() => undefined)
        navigate(INITIAL_PATH, { replace: true })
    }

    const handlePasskeyLogin = async () => {
        if (isLoading) return
        setError(undefined)
        setIsLoading(true)
        try {
            await signInWithPasskey()
        } catch {
            setError('Passkey sign in failed')
        } finally {
            setIsLoading(false)
        }
    }

    const handleMfaPasskeyLogin = async () => {
        if (isLoading) return
        setMfaError(undefined)
        setIsLoading(true)
        try {
            await signInWithPasskey()
        } catch {
            setMfaError('Passkey sign in failed. You can still use your authenticator code.')
        } finally {
            setIsLoading(false)
        }
    }

    const handleSsoLogin = async () => {
        if (isLoading) return
        setError(undefined)
        setIsLoading(true)
        try {
            window.location.href = await billingApi.getSsoAzureLoginUrl()
        } catch {
            setError('Failed fetching login url')
        }
        setIsLoading(false)
    }

    let onPasskeyLogin: (() => void) | undefined
    if (passkeysSupported()) onPasskeyLogin = () => void handlePasskeyLogin()
    let onMfaPasskeyLogin: (() => Promise<void>) | undefined
    if (mfaChallenge?.passkeyAvailable) onMfaPasskeyLogin = signInWithPasskey
    let inlineMfa: InlineMfaPrompt | undefined
    if (mfaChallenge?.state === 'totpRequired') {
        inlineMfa = { error: mfaError, onCredentialsChanged: clearInlineMfa }
        if (mfaChallenge.passkeyAvailable) inlineMfa.onPasskeyLogin = () => void handleMfaPasskeyLogin()
    }

    return (
        <>
            <LoginDialog
                onLogin={(credentials) => void handleLogin(credentials)}
                loading={isLoading}
                error={error}
                logoSrc={netiLogoWhite}
                title="Billing Manager"
                ssoLoginMethods={[{ strategy: 'azure', displayName: 'Sign in with Azure' }]}
                onSsoLogin={() => void handleSsoLogin()}
                onPasskeyLogin={onPasskeyLogin}
                inlineMfa={inlineMfa}
            />
            {mfaChallenge?.state === 'totpEnrollmentRequired' && (
                <TotpLoginDialog
                    challenge={mfaChallenge}
                    api={billingApi.totpLogin}
                    onAuthenticated={(result) => finishLogin(result, mfaChallenge.state === 'totpEnrollmentRequired')}
                    onCancel={() => setMfaChallenge(undefined)}
                    onUsePasskey={onMfaPasskeyLogin}
                />
            )}
        </>
    )
}
